import {Component, EventEmitter, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {mapCategory, ModuleCategoryInfo, resolveModuleIcon} from './workflow.models';
import {ModuleManifest} from '../../api/model/moduleManifest';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {BicInputComponent} from '@buildinginformationcloud/bic-ui-components';
import {faMagnifyingGlass} from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'app-workflow-sidebar',
  standalone: true,
  imports: [FormsModule, FaIconComponent, BicInputComponent],
  templateUrl: './workflow-sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {'class': 'flex flex-col h-full min-h-0'},
})
export class WorkflowSidebarComponent {
  @Input() categories: ModuleCategoryInfo[] = [];
  @Input() modules: ModuleManifest[] = [];
  @Input() sharedCategories: ModuleCategoryInfo[] = [];
  @Input() sectionTitle = 'Module';
  @Input() searchQuery = '';
  /** Filter modules by moduleType (WORKFLOW or PRUEFREGEL). If not set, all modules are shown. */
  @Input() moduleTypeFilter: string | null = null;
  @Output() searchQueryChange = new EventEmitter<string>();
  protected readonly faMagnifyingGlass = faMagnifyingGlass;

  get regularModules(): ModuleManifest[] {
    const regular = this.modulesByType.filter(m => !this.isSharedModule(m));
    return this.filterAndSortModules(regular, this.categories);
  }

  get sharedModules(): ModuleManifest[] {
    const shared = this.modulesByType.filter(m => this.isSharedModule(m));
    return this.filterAndSortModules(shared, this.sharedCategories);
  }

  /** Modules filtered by moduleType only */
  private get modulesByType(): ModuleManifest[] {
    if (!this.moduleTypeFilter) return this.modules;
    return this.modules.filter(m => m.moduleType === this.moduleTypeFilter);
  }

  getCategoryInfo(mod: ModuleManifest): ModuleCategoryInfo | null {
    const sharedInfo = this.getSharedCategoryInfo(mod);
    if (sharedInfo) return sharedInfo;

    const cat = mapCategory(mod.category);
    return this.categories.find(c => c.category === cat) ?? null;
  }

  /** The module's own FontAwesome icon, falling back to its category icon. */
  getModuleIcon(mod: ModuleManifest): IconDefinition | null {
    return resolveModuleIcon(mod.icon) ?? this.getCategoryInfo(mod)?.icon ?? null;
  }

  onDragStart(event: DragEvent, mod: ModuleManifest): void {
    event.dataTransfer?.setData('application/workflow-module', JSON.stringify(mod));
    event.dataTransfer!.effectAllowed = 'copy';
  }

  private filterAndSortModules(modules: ModuleManifest[], categoryOrder: ModuleCategoryInfo[]): ModuleManifest[] {
    const query = this.searchQuery?.trim().toLowerCase();
    const filtered = query
      ? modules.filter(m =>
        m.name.toLowerCase().includes(query)
        || (m.description ?? '').toLowerCase().includes(query)
        || m.moduleId.toLowerCase().includes(query)
      )
      : modules;

    const order = new Map(categoryOrder.map((cat, idx) => [cat.category, idx]));

    return [...filtered].sort((a, b) => {
      const aOrder = order.get(mapCategory(a.category)) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = order.get(mapCategory(b.category)) ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name, 'de');
    });
  }

  private isSharedModule(mod: ModuleManifest): boolean {
    return this.getSharedCategoryInfo(mod) !== null;
  }

  private getSharedCategoryInfo(mod: ModuleManifest): ModuleCategoryInfo | null {
    if (this.sharedCategories.length === 0) return null;

    const moduleId = mod.moduleId.toLowerCase();
    const moduleName = mod.name.toLowerCase();
    const sharedByName = this.sharedCategories.find(
      category => category.label.toLowerCase() === moduleName
    );
    if (sharedByName) return sharedByName;

    // Hybrid shared modules are currently identified by the dedicated systemprompt module.
    if (moduleId.includes('systemprompt') || moduleName.includes('systemprompt')) {
      return this.sharedCategories[0] ?? null;
    }

    return null;
  }
}
